import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Phone, MessageCircle, Calendar } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  'neu': 'bg-[#d1af6a] text-[#0a1120]',
  'kontaktiert': 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
  'in_bearbeitung': 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
  'abgeschlossen': 'bg-green-500/20 text-green-400 border border-green-500/30',
  'abgelehnt': 'bg-red-500/20 text-red-400 border border-red-500/30'
};

const statusLabels = {
  'neu': 'Neu',
  'kontaktiert': 'Kontaktiert',
  'in_bearbeitung': 'In Bearbeitung',
  'abgeschlossen': 'Abgeschlossen',
  'abgelehnt': 'Abgelehnt'
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('alle');

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Anfragen');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (leadId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/leads/${leadId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status aktualisiert');
      fetchLeads();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const filteredLeads = filter === 'alle' 
    ? leads 
    : leads.filter(l => l.status === filter);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-8" data-testid="admin-leads">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl text-white mb-2">Anfragen</h1>
          <p className="text-[#c8c6c4]">{leads.length} Einträge</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="input-luxury w-[180px]">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent className="bg-[#1a2640] border-[#d1af6a]/20">
            <SelectItem value="alle">Alle</SelectItem>
            <SelectItem value="neu">Neu</SelectItem>
            <SelectItem value="kontaktiert">Kontaktiert</SelectItem>
            <SelectItem value="in_bearbeitung">In Bearbeitung</SelectItem>
            <SelectItem value="abgeschlossen">Abgeschlossen</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="spinner" />
        </div>
      ) : filteredLeads.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-[#c8c6c4]">Keine Anfragen gefunden.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredLeads.map((lead) => (
            <div key={lead.id} className="card-luxury p-6">
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`px-2 py-1 text-xs font-medium ${statusColors[lead.status] || statusColors['neu']}`}>
                      {statusLabels[lead.status] || 'Neu'}
                    </span>
                    <span className="text-[#d1af6a] text-xs font-accent tracking-wider">
                      {lead.form_type === 'valuation' ? 'WERTERMITTLUNG' : 
                       lead.form_type === 'contact' ? 'KONTAKT' : 
                       lead.service_type?.toUpperCase() || 'ANFRAGE'}
                    </span>
                  </div>
                  <h3 className="text-white font-medium text-lg mb-2">{lead.name}</h3>
                  <div className="flex flex-wrap gap-4 text-[#c8c6c4] text-sm mb-4">
                    <a href={`mailto:${lead.email}`} className="flex items-center gap-2 hover:text-[#d1af6a]">
                      <Mail className="w-4 h-4" />
                      {lead.email}
                    </a>
                    {lead.phone && (
                      <a href={`tel:${lead.phone}`} className="flex items-center gap-2 hover:text-[#d1af6a]">
                        <Phone className="w-4 h-4" />
                        {lead.phone}
                      </a>
                    )}
                    {lead.phone && (
                      <a href={`https://wa.me/${lead.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#d1af6a]">
                        <MessageCircle className="w-4 h-4" />
                        WhatsApp
                      </a>
                    )}
                  </div>
                  {(lead.message || lead.special_features) && (
                    <p className="text-[#c8c6c4] text-sm bg-[#131d30] p-3 border-l-2 border-[#d1af6a]/30">
                      {lead.message || lead.special_features}
                    </p>
                  )}
                  {lead.property_type && (
                    <div className="mt-3 text-[#c8c6c4] text-sm">
                      <span className="text-[#d1af6a]">Objektart:</span> {lead.property_type}
                      {lead.zip_code && <span className="ml-4"><span className="text-[#d1af6a]">PLZ:</span> {lead.zip_code}</span>}
                      {lead.living_area && <span className="ml-4"><span className="text-[#d1af6a]">Fläche:</span> {lead.living_area} m²</span>}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-3">
                  <div className="flex items-center gap-2 text-[#c8c6c4] text-xs">
                    <Calendar className="w-4 h-4" />
                    {formatDate(lead.created_at)}
                  </div>
                  <Select value={lead.status} onValueChange={(v) => updateStatus(lead.id, v)}>
                    <SelectTrigger className="input-luxury w-[160px] h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a2640] border-[#d1af6a]/20">
                      <SelectItem value="neu">Neu</SelectItem>
                      <SelectItem value="kontaktiert">Kontaktiert</SelectItem>
                      <SelectItem value="in_bearbeitung">In Bearbeitung</SelectItem>
                      <SelectItem value="abgeschlossen">Abgeschlossen</SelectItem>
                      <SelectItem value="abgelehnt">Abgelehnt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
